import { collection, query, where, getDocs, deleteDoc } from 'firebase/firestore';
import { resolvePartyLabelsForCase } from './caseTaxonomyRegistry';

export { generateCaseTitleFromPartyText } from './caseTaxonomyRegistry';

/** LexGat-provided template catalog — empty by default; add your own via custom templates. */
export const TEMPLATE_CATEGORIES: Record<string, string[]> = {};

export const TEMPLATE_SIDES: Record<string, string[]> = {};

export const DOCUMENT_TYPES = Array.from(new Set(Object.values(TEMPLATE_CATEGORIES).flat()));

export function getTemplatesForCaseType(caseType: string | undefined | null): string[] {
  if (!caseType || !TEMPLATE_CATEGORIES[caseType]) {
    return DOCUMENT_TYPES;
  }
  return TEMPLATE_CATEGORIES[caseType];
}

type PartyLike = { name?: string } | string;

function partyLinesFromField(value: unknown): PartyLike[] {
  if (!value) return [];
  if (Array.isArray(value)) return value as PartyLike[];
  if (typeof value === 'string') {
    return value
      .split(/\n+/)
      .map((line) => line.trim())
      .filter(Boolean)
      .map((name) => ({ name: name.replace(/^\d+\.\s*/, '') }));
  }
  return [];
}

/** Normalize Firestore case shapes for template variable substitution. */
export function normalizeCaseForTemplateVariables(caseData: Record<string, unknown> | null | undefined) {
  if (!caseData) return {};
  const c = caseData;

  const courtRaw =
    c.assignedCourt || c.court || c.courtType || c.forum || c.courtName || '';
  const districtRaw = c.district || c.districtName || c.city || '';

  const plaintiffs = [
    ...partyLinesFromField(c.plaintiffs),
    ...partyLinesFromField(c.petitioners),
    ...partyLinesFromField(c.applicants),
    ...partyLinesFromField(c.complainants),
    ...partyLinesFromField(c.appellants),
    ...partyLinesFromField(c.partyA),
  ];
  const defendants = [
    ...partyLinesFromField(c.defendants),
    ...partyLinesFromField(c.respondents),
    ...partyLinesFromField(c.accused),
    ...partyLinesFromField(c.partyB),
  ];

  return {
    ...c,
    title: c.title || c.caseTitle || c.clientName || '',
    courtType: c.courtType || c.court || c.assignedCourt || '',
    assignedCourt: courtRaw,
    district: districtRaw,
    caseNumber: c.caseNumber || c.caseNum || '',
    plaintiffs: plaintiffs.length ? plaintiffs : c.plaintiffs,
    defendants: defendants.length ? defendants : c.defendants,
  };
}

export function replaceTemplateVariables(html: string, caseData: any = null, userData: any = null): string {
  if (!caseData && !userData && !html.includes('{{')) return html;

  const c = normalizeCaseForTemplateVariables(
    caseData && typeof caseData === 'object' ? (caseData as Record<string, unknown>) : null,
  );
  const caseTitle = String(c.title || '');
  const courtRaw = c.assignedCourt || c.court || c.courtType || '';
  const courtName = courtRaw ? String(courtRaw).toUpperCase() : '';
  const district = c.district ? String(c.district).toUpperCase() : '';
  const caseNum = String(c.caseNumber || '_______');

  const extractNamesAsList = (arr: PartyLike[]) => {
    if (!arr || !Array.isArray(arr) || arr.length === 0) return '';
    const names = arr
      .map((p) => (typeof p === 'string' ? p : p?.name || ''))
      .map((n) => n.trim())
      .filter(Boolean);
    if (names.length === 0) return '';
    if (names.length === 1) return names[0];
    return names.map((name, i) => `${i + 1}. ${name}`).join('<br/>');
  };

  const partyA = extractNamesAsList(partyLinesFromField(c.plaintiffs));
  const partyB = extractNamesAsList(partyLinesFromField(c.defendants));

  const now = new Date();
  const month = (now.getMonth() + 1).toString().padStart(2, '0');
  const year = now.getFullYear();
  const date = `____.${month}.${year}`;
  const lawyerDesignation = userData?.profileData?.designation || 'Advocate High Court';
  const lawyerName = userData?.profileData?.fullName || userData?.displayName || userData?.email?.split('@')[0] || c.lawyer?.name || c.lawyerName || '[Your Name]';

  let result = html;

  const replacePlaceholder = (pattern: RegExp, value: string, fallback: string) => {
    return result.replace(pattern, value || fallback);
  };

  const ph = (name: string) => new RegExp(`\\{\\{\\s*${name}\\s*\\}\\}`, 'gi');

  result = replacePlaceholder(ph('caseTitle'), caseTitle, '__________');
  result = replacePlaceholder(ph('courtName'), courtName, '[COURT NAME]');
  result = replacePlaceholder(ph('Court'), courtName, '[COURT NAME]');
  result = replacePlaceholder(ph('district'), district, '[DISTRICT]');
  result = replacePlaceholder(ph('caseNumber'), caseNum, '_______');
  result = replacePlaceholder(ph('caseNum'), caseNum, '_______');

  const sideLabels = resolvePartyLabelsForCase(c);
  const partyAFallback = sideLabels.party1Label;
  const partyBFallback = sideLabels.party2Label;

  result = result.replace(ph('PartyAList'), partyA || partyAFallback);
  result = result.replace(ph('PartyBList'), partyB || partyBFallback);

  result = result.replace(ph('plaintiffs?'), partyA || partyAFallback);
  result = result.replace(ph('petitioners?'), partyA || partyAFallback);
  result = result.replace(ph('applicants?'), partyA || partyAFallback);
  result = result.replace(ph('complainants?'), partyA || partyAFallback);
  result = result.replace(ph('appellants?'), partyA || partyAFallback);

  result = result.replace(ph('defendants?'), partyB || partyBFallback);
  result = result.replace(ph('respondents?'), partyB || partyBFallback);
  result = result.replace(ph('accused'), partyB || partyBFallback);

  result = result.replace(ph('date'), date);
  result = result.replace(ph('lawyer'), `${lawyerName}<br/>${lawyerDesignation}`);

  return result;
}

/** Minimal blank scaffold for new documents — no LexGat-provided content. */
export function getTemplateScaffold(type: string, _caseData: any = null, _userData: any = null): string {
  return getWorkspaceTemplateStarterScaffold(type);
}

/** Default workspace template body with merge-field placeholders for lawyers to customize. */
export function getWorkspaceTemplateStarterScaffold(title: string): string {
  const heading = (title || 'Untitled Document').trim();
  return `
<p style="text-align: center;"><strong>IN THE COURT OF {{Court}}, {{district}}</strong></p>
<p style="text-align: center;"><strong>Case No. {{caseNumber}}</strong></p>
<p style="text-align: center;"><strong>${heading.toUpperCase()}</strong></p>
<p><br></p>
<p><strong>{{PartyAList}}</strong></p>
<p style="text-align: center;"><strong>VERSUS</strong></p>
<p><strong>{{PartyBList}}</strong></p>
<p><br></p>
<p><strong>Respectfully Sheweth:</strong></p>
<p>1. That …</p>
<p><br></p>
<p><strong>PRAYER</strong></p>
<p>It is therefore most respectfully prayed that …</p>
<p><br></p>
<p>Dated: {{date}}</p>
<p><br></p>
<div style="margin-left: 60%; text-align: left;">
<p><strong>Through</strong><br/>{{lawyer}}</p>
</div>
  `.trim();
}

/** Remove all LexGat system templates from Firestore. */
export async function clearSystemTemplates(db: any): Promise<number> {
  const templatesRef = collection(db, 'workspaces', 'SYSTEM', 'documents');
  const q = query(templatesRef, where('isSystem', '==', true));
  const snapshot = await getDocs(q);

  await Promise.all(snapshot.docs.map((docSnap) => deleteDoc(docSnap.ref)));
  return snapshot.size;
}
